import { Text, Flex, Box, Grid, GridItem } from '@everlywell/ui-kit';
import React, { useState, useEffect, useRef } from 'react';

import { IStatsBarProps } from '../../utils/types';

const StatsBar: React.FC<IStatsBarProps> = ({
  stats,
  disclaimer,
  backgroundColor,
  textColor,
  labelColor,
}) => {
  const [counts, setCounts] = useState<number[]>(() =>
    new Array(stats.length).fill(0),
  );
  const [isAnimating, setIsAnimating] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsAnimating(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isAnimating) return;

    const duration = 2000;
    const steps = 60;
    const stepDuration = duration / steps;
    let frame = 0;

    // Single interval instead of multiple
    const interval = setInterval(() => {
      frame++;
      setCounts((prevCounts) =>
        prevCounts.map((count, index) => {
          const target = Number(stats[index].value);
          return Math.min(count + target / steps, target);
        }),
      );

      if (frame >= steps) {
        setIsAnimating(false);
        clearInterval(interval);
      }
    }, stepDuration);

    return () => {
      clearInterval(interval);
      setIsAnimating(false);
    };
  }, [isAnimating, stats]);

  return (
    <Box
      as="section"
      ref={containerRef}
      px={['0.5rem', '1rem', '1rem']}
      mx="8px"
    >
      <Grid
        width="100%"
        maxWidth="1440px"
        mx="auto"
        backgroundColor={backgroundColor}
        borderRadius="40px"
        justifyContent={['center', 'space-around']}
        gap={['10px', '0']}
        aria-label="Key statistics"
        role="group"
      >
        {stats.map((stat, index) => (
          <GridItem
            key={index}
            data-testid={`mock-grid-item-${index}`}
            p={['1.25rem', '2rem']}
            width={['calc(50% - 1rem)', 'calc(50% - 1rem)', '25%']}
            display="flex"
            flexDirection="column"
            gap="0.5rem"
            aria-label={`${stat.label} statistic`}
            role="article"
          >
            <Flex alignItems="baseline" gap="0.25rem">
              {stat.prefix && (
                <Text
                  as="span"
                  color={textColor}
                  fontSize={['1.8rem', '2.25rem']}
                  lineHeight={['2rem', '2.7rem']}
                  aria-hidden="true"
                >
                  {stat.prefix}
                </Text>
              )}
              <Text
                as="span"
                fontFamily="PP Agrandir"
                color="#143927"
                fontSize={['45px', '3rem']}
                fontWeight="400"
                lineHeight={['4.2rem', '4rem']}
                data-testid={`stat-value-${index}`}
                aria-label={`${counts[index]} ${stat.suffix}`}
              >
                {counts[index].toFixed(
                  String(stats[index].value).split('.')[1]?.length || 0,
                )}
              </Text>
              <Text
                as="span"
                color={textColor}
                fontSize={['1.8rem', '2.25rem']}
                fontWeight="500"
                lineHeight="2.7rem"
                aria-hidden="true"
              >
                {stat.suffix}
              </Text>
            </Flex>
            <Text
              as="p"
              color={labelColor || '#143927'}
              fontSize={['14px', '16px']}
              fontFamily="DM Sans"
              lineHeight="1.125rem"
            >
              {stat.label}
            </Text>
          </GridItem>
        ))}
      </Grid>
      {disclaimer && (
        <Text
          as="p"
          width="100%"
          textAlign="center"
          color="#0F0F0F"
          fontSize="0.75rem"
          fontFamily="DM Sans"
          lineHeight="1.125rem"
          mt="2rem"
          mx="auto"
          dangerouslySetInnerHTML={{
            __html: disclaimer.childMarkdownRemark.html,
          }}
          sx={{
            a: {
              color: '#28724F',
            },
          }}
        />
      )}
    </Box>
  );
};

export default StatsBar;
